import React, { FC, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Form, Icon } from 'semantic-ui-react';
import type { SemanticICONS } from 'semantic-ui-react';
import { Flexbox } from '@src/views/containers';
import { FlexAlign, FlexJustify } from '@src/types';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size } from '@src/types';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { LogHoursConsultantOption, notifyOfferingHoursLogged, useConsultantOptions } from './offeringHoursData';
import { durationMsToLoggedMinutes, toLoggedMinutes } from './timeClockFormat';

export type { LogHoursConsultantOption } from './offeringHoursData';

const HOURS_TABLE = 'ConsultantOfferingHours';
const NOTE_MAX_LENGTH = 2500;
const MAX_HOURS = 32767;
const MAX_LOGGED_MINUTES = MAX_HOURS * 60 + 59;

// Stored as free text in ConsultingClientOfferingHours.HoursType (nvarchar(20), defaults to 'Standard').
export type HoursType = 'Standard' | 'Travel';

const HOURS_TYPE_OPTIONS: ReadonlyArray<{ value: HoursType; label: string; icon: SemanticICONS }> = [
    { value: 'Standard', label: 'Standard', icon: 'briefcase' },
    { value: 'Travel', label: 'Travel', icon: 'car' },
];

export interface LogHoursPrefill {
    hours: number;
    minutes: number;
    date: string;
    trackedLabel: string;
    startTime?: string;
    endTime?: string;
}

interface LogHoursDialogProps {
    clientOfferingId: string;
    // Omit to have the dialog load consultants itself (e.g. when opened from the header clock).
    consultants?: LogHoursConsultantOption[];
    prefill?: LogHoursPrefill;
    onClose: () => void;
    onSaved?: () => Promise<void> | void;
}

const todayIsoDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${ now.getMonth() + 1 }`.padStart( 2, '0' );
    const day = `${ now.getDate() }`.padStart( 2, '0' );
    return `${ year }-${ month }-${ day }`;
};

const parseNonNegativeInteger = ( value: string ): number | null => {
    const trimmed = value.trim();
    if( trimmed === '' ) return 0;
    if( !/^\d+$/.test( trimmed ) ) return null;

    const parsed = Number( trimmed );
    return Number.isSafeInteger( parsed ) ? parsed : null;
};

const LogHoursDialog: FC<LogHoursDialogProps> = ( {
    clientOfferingId,
    consultants: providedConsultants,
    prefill,
    onClose,
    onSaved,
} ) => {
    const { RunInsert } = useCentralCalls();
    const { consultants: loadedConsultants, isLoading: isLoadingConsultants } = useConsultantOptions( !providedConsultants );
    const consultants = providedConsultants ?? loadedConsultants;

    const [ consultantId, setConsultantId ] = useState( consultants[ 0 ]?.id ?? '' ),
          [ hoursInput, setHoursInput ]     = useState( prefill ? `${ prefill.hours }` : '' ),
          [ minutesInput, setMinutesInput ] = useState( prefill ? `${ prefill.minutes }` : '' ),
          [ hoursType, setHoursType ]       = useState<HoursType>( 'Standard' ),
          [ dateTracking, setDateTracking ] = useState( prefill?.date ?? todayIsoDate() ),
          [ note, setNote ]                 = useState( '' ),
          [ isSaving, setIsSaving ]         = useState( false ),
          [ error, setError ]               = useState( '' );

    useEffect( () => {
        if( !consultantId && consultants.length > 0 ) setConsultantId( consultants[ 0 ].id );
    }, [ consultants, consultantId ] );

    const consultantOptions = useMemo(
        () => consultants.map( consultant => ( {
            key: consultant.id,
            value: consultant.id,
            text: consultant.name,
        } ) ),
        [ consultants ]
    );

    const handleSubmit = async () => {
        if( isSaving ) return { errors: 'Saving...' };

        if( !clientOfferingId ){
            const message = 'This offering is missing a ClientOfferingId, so hours cannot be saved.';
            setError( message );
            return { errors: message };
        }

        if( !consultantId ){
            const message = 'Select a consultant.';
            setError( message );
            return { errors: message };
        }

        const parsedHours = parseNonNegativeInteger( hoursInput );
        if( parsedHours == null || parsedHours > MAX_HOURS ){
            const message = `Enter hours as a whole number from 0 to ${ MAX_HOURS }.`;
            setError( message );
            return { errors: message };
        }

        const parsedMinutes = parseNonNegativeInteger( minutesInput );
        if( parsedMinutes == null || parsedMinutes > 59 ){
            const message = 'Enter minutes as a whole number from 0 to 59.';
            setError( message );
            return { errors: message };
        }

        let loggedMinutes = toLoggedMinutes( parsedHours, parsedMinutes );
        let startTime = prefill?.startTime;
        let endTime = prefill?.endTime;

        // Timer entries keep the clock bounds. An unedited duration is the minutes between them.
        if( startTime ){
            endTime = endTime ?? new Date().toISOString();
            const durationEdited = parsedHours !== prefill?.hours || parsedMinutes !== prefill?.minutes;
            if( !durationEdited ){
                const elapsed = new Date( endTime ).getTime() - new Date( startTime ).getTime();
                if( !Number.isNaN( elapsed ) && elapsed >= 0 ){
                    loggedMinutes = durationMsToLoggedMinutes( elapsed );
                }
            }
        }

        if( loggedMinutes < 1 ){
            const message = 'Enter at least 1 minute.';
            setError( message );
            return { errors: message };
        }

        if( loggedMinutes > MAX_LOGGED_MINUTES ){
            const message = `Enter ${ MAX_HOURS } hours or fewer.`;
            setError( message );
            return { errors: message };
        }

        const trimmedNote = note.trim();
        if( trimmedNote.length > NOTE_MAX_LENGTH ){
            const message = `Note must be ${ NOTE_MAX_LENGTH } characters or fewer.`;
            setError( message );
            return { errors: message };
        }

        setIsSaving( true );
        setError( '' );

        try {
            await RunInsert( HOURS_TABLE, {
                Id: uuid(),
                ConsultantId: consultantId,
                ClientOfferingId: clientOfferingId,
                Hours: Math.min( MAX_HOURS, Math.floor( loggedMinutes / 60 ) ),
                LoggedMinutes: loggedMinutes,
                ...( startTime ? { StartTime: startTime } : {} ),
                ...( endTime ? { EndTime: endTime } : {} ),
                HoursType: hoursType,
                DateTracking: dateTracking || todayIsoDate(),
                Note: trimmedNote || null,
            } );

            notifyOfferingHoursLogged( clientOfferingId );
            await onSaved?.();
            onClose();
        } catch( err ){
            console.error( '[LogHoursDialog] failed to log hours:', err );
            const message = err instanceof Error ? err.message : 'Could not log hours. Try again.';
            setError( message );
            return { errors: message };
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton
            submitLabel={ isSaving ? 'Saving...' : 'Log hours' }
            submitColor={ SemanticUIColor.PURPLE }
            operationDisabled={ isSaving || consultants.length === 0 }
            successToastLabel={ 'Hours logged' }
            errorToastLabel={ '' }
            onCancel={ onClose }
            onSubmit={ handleSubmit }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog log-hours-dialog' }
        >
            <BasicDialogContainer
                title={ 'Log hours' }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '28dvh' }
                overflow
                onClose={ onClose }
            >
                <Form className={ 'create-client-form log-hours-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' } /> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Hours for this offering</span>
                        </div>

                        { prefill && (
                            <p className={ 'log-hours-timer-hint' }>
                                <Icon name={ 'clock outline' } />
                                Timer tracked { prefill.trackedLabel }. Adjust the hours and minutes if needed.
                            </p>
                        ) }

                        <Form.Select
                            label={ 'Consultant' }
                            search
                            loading={ isLoadingConsultants }
                            options={ consultantOptions }
                            value={ consultantId }
                            placeholder={ isLoadingConsultants ? 'Loading consultants…' : consultants.length === 0 ? 'No consultants found' : 'Select consultant' }
                            disabled={ isSaving || consultants.length === 0 }
                            onChange={ ( _event, data ) => {
                                setConsultantId( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />

                        <Form.Field>
                            <label id={ 'log-hours-type-label' }>Type</label>
                            <Flexbox
                                align={ FlexAlign.CENTER }
                                justify={ FlexJustify.START }
                                noWrap
                                className={ 'log-hours-type-toggle' }
                                role={ 'radiogroup' }
                                aria-labelledby={ 'log-hours-type-label' }
                            >
                                { HOURS_TYPE_OPTIONS.map( option => {
                                    const isSelected = hoursType === option.value;
                                    return (
                                        <button
                                            key={ option.value }
                                            type={ 'button' }
                                            role={ 'radio' }
                                            aria-checked={ isSelected }
                                            disabled={ isSaving }
                                            className={ `log-hours-type-option${ isSelected ? ' is-selected' : '' }` }
                                            onClick={ () => setHoursType( option.value ) }
                                        >
                                            <Icon name={ option.icon } />
                                            { option.label }
                                        </button>
                                    );
                                } ) }
                            </Flexbox>
                        </Form.Field>

                        <div className={ 'log-hours-form-row' }>
                            <Form.Input
                                label={ 'Hours' }
                                type={ 'number' }
                                min={ 0 }
                                max={ MAX_HOURS }
                                step={ 1 }
                                value={ hoursInput }
                                placeholder={ '0' }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setHoursInput( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />

                            <Form.Input
                                label={ 'Minutes' }
                                type={ 'number' }
                                min={ 0 }
                                max={ 59 }
                                step={ 1 }
                                value={ minutesInput }
                                placeholder={ '0' }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setMinutesInput( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />

                            <Form.Input
                                className={ 'log-hours-date-field' }
                                label={ 'Date' }
                                type={ 'date' }
                                value={ dateTracking }
                                disabled={ isSaving }
                                onChange={ ( _event, data ) => {
                                    setDateTracking( String( data.value ?? '' ) );
                                    if( error ) setError( '' );
                                } }
                            />
                        </div>

                        <Form.TextArea
                            label={ 'Note' }
                            value={ note }
                            maxLength={ NOTE_MAX_LENGTH }
                            placeholder={ 'Optional note' }
                            disabled={ isSaving }
                            rows={ 3 }
                            onChange={ ( _event, data ) => {
                                setNote( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default LogHoursDialog;
